import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import { formatName } from '@/utils/name';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DialogProps } from '@toolpad/core/useDialogs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { StudentData } from '../types/student';

export function StudentEditDialog({ payload, open, onClose }: DialogProps<{ data?: { user: StudentData } }, StudentData | null>) {
    const { t } = useTranslation();

    // ui
    const [saving, setSaving] = React.useState(false);
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (emailError) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const familyName = data.get('last-name') as string;
        const givenName = data.get('first-name') as string;
        const studentNumber = data.get('student-number') as string;
        const email = data.get('email') as string;
        const classId = data.get('class-select') as string;

        setSaving(true);
        if (payload.data) {
            await onClose({
                id: payload.data.user.id,
                studentProfileId: payload.data.user.studentProfileId,
                class: payload.data.user.classes.find((c) => c.id === classId) || payload.data.user.class,
                studentNumber,
                familyName,
                givenName,
                email,
                name: formatName(familyName, givenName),
                classes: payload.data.user.classes,
            });
        } else {
            await onClose(null);
        }
        setSaving(false);
    };

    const validateInputs = () => {
        const email = document.getElementById('email') as HTMLInputElement;

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage(t('common.form.invalidEmail'));
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        return isValid;
    };

    return (
        <Dialog fullWidth open={open} onClose={() => onClose(null)}>
            <Box component='form' onSubmit={handleSubmit} noValidate>
                <DialogTitle>{t('student.actions.editStudent')}</DialogTitle>
                <DialogContent>
                    <Stack sx={{ pt: 1 }} gap={2}>
                        <Stack direction={'row'} gap={2}>
                            <TextField id='first-name' name='first-name' label={t('student.label.givenName')} fullWidth defaultValue={payload.data?.user.givenName} />
                            <TextField id='last-name' name='last-name' label={t('student.label.familyName')} defaultValue={payload.data?.user.familyName} fullWidth />
                        </Stack>
                        <FormControl required>
                            <InputLabel id='class-select-label'>{t('student.label.class')}</InputLabel>
                            <Select labelId='class-select-label' id='class-select' label={t('student.label.class')} name='class-select' defaultValue={payload.data?.user.class.id || ''}>
                                {payload.data?.user.classes.map((c) => {
                                    const name = c.name || '';
                                    const avatarColor = generateAvatarColor(name);
                                    const bg = avatarColor?.bg;
                                    const text = avatarColor?.text;

                                    return (
                                        <MenuItem key={c.id} value={c.id}>
                                            <Stack direction='row' spacing={1} alignItems='center'>
                                                <InitialalsAvatar
                                                    sx={{
                                                        bgcolor: bg,
                                                        color: text,
                                                    }}
                                                >
                                                    {getInitials(name)}
                                                </InitialalsAvatar>
                                                <Typography>{`${c.name} (${c.schoolYear})`}</Typography>
                                            </Stack>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        <TextField id='student-number' name='student-number' label={t('student.label.number')} defaultValue={payload.data?.user.studentNumber} />
                        <TextField
                            id='email'
                            label={t('student.label.email')}
                            defaultValue={payload.data?.user.email}
                            type='email'
                            name='email'
                            error={emailError}
                            helperText={emailErrorMessage}
                            placeholder='student@email.com'
                            color={emailError ? 'error' : 'primary'}
                            required
                            autoComplete='email'
                        />
                    </Stack>
                </DialogContent>
                <Stack direction='row' sx={{ justifyContent: 'flex-end', gap: 1, p: 1 }}>
                    <Button
                        onClick={async () => {
                            onClose(null);
                        }}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button loading={saving} variant='contained' type='submit' onClick={validateInputs} loadingIndicator={<CircularProgress size={16} />}>
                        {t('common.save')}
                    </Button>
                </Stack>
            </Box>
        </Dialog>
    );
}
